import React from 'react';
import {
  EditOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  CheckCircleFilled,
  RightCircleFilled,
  LockOutlined,
} from '@ant-design/icons';
import cn from 'classnames';
import Tag from 'components/Tag';
import { getCurrencyValue } from 'utils/number';
import { ORDER_FLOW_TYPES } from '../../constants';
import classes from './CartSidebar.module.scss';

const CartSidebar = ({
  cartOrders,
  cartTotal,
  editingCartIndex,
  onEdit,
  onDelete,
  orderFlowType,
  selectedPackage,
  packageOrderIndex,
}) => {
  const isPackageFlow = orderFlowType === ORDER_FLOW_TYPES.PACKAGE;

  // Show sidebar when:
  // - simple flow with cart items
  // - package flow with a selected package
  if (!isPackageFlow && (!cartOrders || cartOrders.length === 0)) return null;
  if (isPackageFlow && !selectedPackage) return null;

  if (isPackageFlow && selectedPackage) {
    const orderTypes = selectedPackage.order_types || [];
    const completedCount = cartOrders ? cartOrders.length : 0;

    return (
      <div className={classes.sidebar}>
        <div className={classes.header}>
          <ShoppingCartOutlined className={classes.headerIcon} />
          <span className={classes.headerTitle}>Package Order</span>
          <Tag className={classes.countTag}>
            {completedCount}/{orderTypes.length}
          </Tag>
        </div>
        <div className={classes.packageName}>{selectedPackage.name}</div>
        <div className={classes.list}>
          {orderTypes.map((ot, index) => {
            const isCompleted =
              index < completedCount && editingCartIndex !== index;
            const isActive = index === packageOrderIndex && !isCompleted;
            const isPending = !isCompleted && !isActive;
            const snapshot = cartOrders && cartOrders[index];
            const fallbackPrice = Number(snapshot?.formValues?.price) || 0;
            const fallbackRush =
              snapshot?.formValues?.rush_order?.length > 0
                ? Number(snapshot?.orderTypeDetail?.rush_amount) || 0
                : 0;

            const price =
              snapshot?.computedPrice ?? fallbackPrice + fallbackRush;

            return (
              <div
                key={ot.id}
                className={cn(classes.packageItem, {
                  [classes.packageItemCompleted]: isCompleted,
                  [classes.packageItemActive]: isActive,
                  [classes.packageItemPending]: isPending,
                })}
              >
                <div className={classes.packageItemStatus}>
                  {isCompleted && (
                    <CheckCircleFilled
                      className={classes.statusIconCompleted}
                    />
                  )}
                  {isActive && (
                    <RightCircleFilled className={classes.statusIconActive} />
                  )}
                  {isPending && (
                    <LockOutlined className={classes.statusIconPending} />
                  )}
                </div>
                <div className={classes.packageItemBody}>
                  <span className={classes.packageItemIndex}>{index + 1}.</span>
                  <span className={classes.packageItemName}>{ot.name}</span>
                  {isCompleted && price !== null && (
                    <span className={classes.packageItemPrice}>
                      {getCurrencyValue(price)}
                    </span>
                  )}
                </div>
                {isCompleted && (
                  <div className={classes.packageItemActions}>
                    <EditOutlined
                      className={classes.packageEditIcon}
                      title="Edit this order"
                      onClick={() => onEdit(index)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Simple flow
  return (
    <div className={classes.sidebar}>
      <div className={classes.header}>
        <ShoppingCartOutlined className={classes.headerIcon} />
        <span className={classes.headerTitle}>Orders in Cart</span>
        <Tag className={classes.countTag}>{cartOrders.length}</Tag>
      </div>
      <div className={classes.list}>
        {cartOrders.map((order, index) => {
          const fallbackPrice = Number(order.formValues?.price) || 0;
          const fallbackRush =
            order.formValues?.rush_order?.length > 0
              ? Number(order.orderTypeDetail?.rush_amount) || 0
              : 0;

          const total = order.computedPrice ?? fallbackPrice + fallbackRush;
          const isEditing = editingCartIndex === index;

          return (
            <div
              key={order.id}
              className={cn(classes.item, { [classes.editing]: isEditing })}
            >
              {isEditing && <div className={classes.editingBadge}>Editing</div>}
              <div className={classes.itemBody}>
                <div className={classes.itemInfo}>
                  <span className={classes.itemIndex}>{index + 1}.</span>
                  <span className={classes.itemName}>
                    {order.orderTypeName || `Order ${index + 1}`}
                  </span>
                </div>
                <span className={classes.itemPrice}>
                  {getCurrencyValue(total)}
                </span>
              </div>
              <div className={classes.itemActions}>
                <EditOutlined
                  className={classes.editIcon}
                  title="Edit this order"
                  onClick={() => onEdit(index)}
                />
                <DeleteOutlined
                  className={classes.deleteIcon}
                  title="Remove from cart"
                  onClick={() => onDelete(index)}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className={classes.footer}>
        <span>Cart Total</span>
        <span className={classes.cartTotal}>
          {getCurrencyValue(cartTotal || 0)}
        </span>
      </div>
    </div>
  );
};

export default CartSidebar;
